import { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Hook to warn users about unsaved changes before navigation
 * 
 * @param {boolean} hasUnsavedChanges - Whether there are unsaved changes
 * @param {string} message - Warning message to show
 * @returns {object} - Functions to handle navigation
 */
export const useUnsavedChangesWarning = (hasUnsavedChanges, message = 'You have unsaved changes. Are you sure you want to leave?') => {
  const navigate = useNavigate();
  const location = useLocation();
  const skipWarningRef = useRef(false);

  // Handle browser back/forward and page refresh
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, message]);

  // Navigate with optional warning skip
  const navigateWithWarning = useCallback((to, options = {}) => {
    if (hasUnsavedChanges && !skipWarningRef.current) {
      const confirmed = window.confirm(message);
      if (!confirmed) return false;
    }
    skipWarningRef.current = false;
    navigate(to, options);
    return true;
  }, [hasUnsavedChanges, message, navigate]);

  // Navigate without warning (for save + navigate flows)
  const navigateWithoutWarning = useCallback((to, options = {}) => {
    skipWarningRef.current = true;
    navigate(to, options);
  }, [navigate]);

  return {
    navigateWithWarning,
    navigateWithoutWarning,
    currentPath: location.pathname
  };
};

/**
 * Hook to preserve analysis state in session storage
 * 
 * @param {string} key - Storage key
 * @param {any} initialState - Initial state value
 * @returns {[any, function]} - State and setter function
 */
export const usePersistedAnalysisState = (key, initialState) => {
  // Use session storage to persist state during navigation
  const getStoredValue = () => {
    try {
      const item = sessionStorage.getItem(key);
      return item ? JSON.parse(item) : initialState;
    } catch {
      return initialState;
    }
  };

  const [value, setValue] = useState(getStoredValue);

  // Update session storage when value changes
  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Failed to persist state:', error);
    }
  }, [key, value]);

  return [value, setValue];
};

export default useUnsavedChangesWarning;
